import { Link } from 'react-router-dom';
import { FileText, Shield, Package, DollarSign, User, AlertTriangle, Lock, Scale, Mail, Globe } from 'lucide-react';
import SEO from '../components/SEO';
import { COMPLIANCE_DISCLOSURE_TEXT, LEGAL_CONSENT_DOCUMENT_PATH } from '../constants/site';

const Terms = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Terms of Use | Website Terms & Conditions"
        description="Flash Funding Terms of Use. Read our website terms and conditions, intellectual property rights, permitted use policy, and governing law information."
        keywords="Flash Funding terms of use, terms and conditions, website terms, legal terms, user agreement, Australian law"
        canonical="/terms"
      />
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-navy-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight leading-none mb-4">
              Terms of<br />
              <span className="gold-gradient-text">Use</span>
            </h1>
            <p className="font-body text-lg text-white/70">
              {COMPLIANCE_DISCLOSURE_TEXT}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* 1. Introduction */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <FileText size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    1. Introduction
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">1.1.</strong> All access to and use of this Website is subject to these terms and conditions ("Terms of Use"). By accessing and using this Website, you agree to be governed by these Terms of Use. If you do not agree to these Terms of Use, you are no longer permitted to access and use this Website.
                </p>
                <p>
                  <strong className="text-white">1.2.</strong> If you are found in violation of these Terms of Use, your right to access and use this Website will immediately be revoked.
                </p>
                <p>
                  <strong className="text-white">1.3.</strong> This Website is intended for Australian residents only, aged 18 years and over. You should be aware this Website (including products, prices, and product descriptions) is updated frequently. Products previously advertised may be withdrawn at any time, without notice.
                </p>
                <p>
                  <strong className="text-white">1.4.</strong> We may amend or alter these Terms of Use at any time. We recommend you refer to these Terms of Use each time you use this Website. The amended Terms of Use will be effective from the date they are posted on this Website. Your continued use of this Website will constitute an acceptance of these amended Terms of Use.
                </p>
              </div>
            </div>

            {/* 2. Privacy Policy */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Shield size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    2. Privacy Policy
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">2.1.</strong> We uphold your privacy with the utmost importance. Please review our{' '}
                  <a href={LEGAL_CONSENT_DOCUMENT_PATH} target="_blank" rel="noopener noreferrer" className="text-gold-500 hover:underline" data-testid="terms-privacy-policy-link">Privacy Policy</a>
                  {' '}to find out how we use, store, and transfer the information that you provide to us. Your use of this Website is at all times subject to the provisions of our Privacy Policy.
                </p>
              </div>
            </div>

            {/* 3. Third-Party Products */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Package size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    3. Third-Party Products
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">3.1.</strong> You may apply for a number of different products via this Website. These products are not provided by us but are instead provided by third parties. We do not exercise control over these third parties. It is your responsibility to verify and satisfy your personal preferences and requirements before applying for, or obtaining any product. Before applying for or purchasing a product supplied by a third party, you should consult the relevant product disclosure statements or documents. The presence of a product on this Website does not constitute an endorsement by us of the provider, the product, the content of the provider's website, or the activities of the provider.
                </p>
                <p>
                  <strong className="text-white">3.2.</strong> All product prices are given in Australian dollars (AUD $).
                </p>
                <p>
                  <strong className="text-white">3.3.</strong> We are not liable for any loss or damages you or any third party may incur in connection with any product you obtain after using this Website. We are not liable for any acts, omissions, errors, or complications of any third party in connection with that product.
                </p>
                <p>
                  <strong className="text-white">3.4.</strong> This Website contains statements regarding the terms of the products, including features and inclusions. Some of these statements are generalised and/or simplified to facilitate a summary of product comparison. However, not all products are identical, and you should confirm the precise terms of any product that you intend to purchase prior to purchase. Product information supplied on the website is available only to Australian residents unless expressly stated otherwise.
                </p>
                <p>
                  <strong className="text-white">3.5.</strong> By submitting your personal details through this Website, you are able to make an offer to obtain a product from a third-party provider. That offer may be accepted or rejected. The contract for the product will only be concluded once your offer has been accepted by the relevant third-party provider. If your offer is accepted, this triggers the third party to supply said product, subject to the third-party provider's terms and conditions. You will receive written confirmation if your offer has been accepted.
                </p>
              </div>
            </div>

            {/* 4. The Flash Funding Service and How We Are Paid */}
            <div className="bg-navy-700 border border-gold-500/20 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <DollarSign size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    4. The Flash Funding Service and How We Are Paid
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">4.1.</strong> We provide a number of services on this Website allowing users to research and compare finance options, all of which are provided by third parties.
                </p>
                <p>
                  <strong className="text-white">4.2.</strong> Our comparison service is free for use, subject to these Terms of Use. We receive payment (commission) from the third-party product providers in the event of a successful purchase from you.
                </p>
              </div>
            </div>

            {/* 5. Permitted Use */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    5. Permitted Use
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">5.1.</strong> You are only permitted to use this Website for personal use, subject to your compliance with these Terms of Use. Commercial use of this Website is strictly prohibited.
                </p>
                <p>
                  <strong className="text-white">5.2.</strong> You are not permitted to do anything that may affect, for ill or good, the security of this Website or any information or material stored within it.
                </p>
              </div>
            </div>

            {/* 6. Registering an Account */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Lock size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    6. Registering an Account
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">6.1.</strong> This Website allows you to create a personal account linked to you to access certain member services provided by Flash Funding. If you create a personal account, you agree that:
                </p>
                <ul className="ml-6 space-y-2">
                  <li><strong className="text-white/80">(a)</strong> Your Login Details are for your use alone.</li>
                  <li><strong className="text-white/80">(b)</strong> You will store or keep your Login Details secure and confidential at all times and will not disclose them to any other person.</li>
                  <li><strong className="text-white/80">(c)</strong> You are responsible for all actions that take place as a result of access to this Website's services via your Login Details.</li>
                  <li><strong className="text-white/80">(d)</strong> Any breach of these Terms of Use under your Login Details will be treated as a breach by you, and your permission for further use of your account and Website will be revoked.</li>
                </ul>
              </div>
            </div>

            {/* 7. No Advice */}
            <div className="bg-navy-700 border border-red-500/20 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={24} className="text-red-400" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    7. No Advice
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">7.1.</strong> Flash Funding does not offer financial, investment, or other monetary advice. <strong className="text-red-400">Nothing on this Website constitutes financial, investment, or other monetary advice</strong> by us in relation to any product.
                </p>
              </div>
            </div>

            {/* 8. Intellectual Property Rights */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Globe size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    8. Intellectual Property Rights
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">8.1.</strong> All intellectual property rights in any material or content contained in or accessible via this Website are either owned by Flash Funding or are licensed for exclusive or unrestricted use by Flash Funding.
                </p>
              </div>
            </div>

            {/* 9. Exclusions of Liability */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Shield size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    9. Exclusions of Liability
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">9.1.</strong> This Website, content, and services are supplied to you on a strictly "as is" basis, and we make no warranties or guarantees regarding their accuracy or reliability.
                </p>
                <p>
                  <strong className="text-white">9.2.</strong> We do not give any warranty that the Website is free from viruses, malware, or anything else that may have a harmful effect on any technology.
                </p>
              </div>
            </div>

            {/* 10. Indemnity */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Scale size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    10. Indemnity
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">10.1.</strong> You agree to indemnify us, our directors, employees, agents, and contractors for any loss suffered or any expenses that may be incurred arising from a breach of these Terms of Use or your use of this Website.
                </p>
              </div>
            </div>

            {/* 11. Contact Us */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Mail size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    11. Contact Us
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">11.1.</strong> If you have any questions concerning this Website or the services and products we provide, please <Link to="/contact" className="text-gold-500 hover:underline">contact us</Link>.
                </p>
              </div>
            </div>

            {/* 12. General */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <FileText size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    12. General
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">12.1.</strong> If any part of these Terms of Use is determined to be legally invalid or unenforceable, such provision will be replaced by a valid, enforceable provision.
                </p>
                <p>
                  <strong className="text-white">12.2.</strong> These Terms of Use constitute the entire agreement between you and us relating to your access to and use of this Website.
                </p>
              </div>
            </div>

            {/* 13. Governing Law */}
            <div className="bg-navy-700 border border-gold-500/20 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Scale size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    13. Governing Law
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  <strong className="text-white">13.1.</strong> These Terms of Use and your access to and use of this Website are governed by and will be interpreted in accordance with the laws of <strong className="text-white">South Australia and the Commonwealth of Australia</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <p className="text-white/40 font-body text-sm text-center mt-12">
            Last updated: December 2025
          </p>
        </div>
      </section>
    </div>
  );
};

export default Terms;
